import { Header } from '@/sections/layout/Header';
import { Footer } from '@/sections/layout/Footer';
import { PropsWithChildren, ReactNode, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import Head from 'next/head';
import scrollIntoView from 'scroll-into-view';
import { TOP_OFFSET } from '@/constants/offsets';
import Loader from '../Loader';
import { PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { useTranslation } from 'react-i18next';

interface Props {
	variant?: 'inverted' | 'default';
	header?: ReactNode;
	isLoading?: boolean;
	className?: string;
	menuPromoBanners?: PageMenuPromoBanner[];
}

function useScrollToHash(isLoading: boolean | undefined) {
	const { asPath } = useRouter();
	useEffect(() => {
		const hash = asPath.split('#')[1];
		let timeoutId: null | ReturnType<typeof setTimeout> = null;

		if (hash && !isLoading) {
			timeoutId = setTimeout(() => {
				const element = document.getElementById(hash);
				if (element) {
					scrollIntoView(element, { time: 1000, align: { topOffset: TOP_OFFSET, top: 0.1 } });
				}
			}, 0);
		}

		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [asPath, isLoading]);
}

export function Layout(props: PropsWithChildren<Props>) {
	const searchParams = useSearchParams();
	const { t } = useTranslation('core');

	useScrollToHash(props.isLoading);

	if (props.isLoading) {
		return <Loader />;
	}

	return (
		<>
			{searchParams?.size !== undefined && searchParams?.size > 0 && (
				<Head>
					<meta name="robots" content="noindex, nofollow" />
				</Head>
			)}
			<div className={props.className}>
				<a
					href="#main-content"
					className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[51] focus:rounded-lg focus:bg-blue-500 focus:px-4 focus:py-2 focus:text-white focus:shadow-lg focus:ring-2 focus:ring-blue-100 focus:outline-none"
				>
					{t('skipToMainContent')}
				</a>

				<nav>
					{props.header ? (
						props.header
					) : (
						<Header menuPromoBanners={props.menuPromoBanners} variant={props.variant} />
					)}
				</nav>

				<main id="main-content">{props.children}</main>

				<Footer />
			</div>
		</>
	);
}
